import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select } from './ui/select';
import { Label } from './ui/label';
import { IconBox } from './ui/icon-box';
import { masterclassService } from '../lib/masterclass';

const businessTypes = [
  { value: 'coach', label: 'Coach / Trainer' },
  { value: 'consultant', label: 'Consultant / Strategist' },
  { value: 'creator', label: 'Content Creator / Influencer' },
  { value: 'agency', label: 'Agency Owner' },
  { value: 'other', label: 'Other' },
];

interface Props { variant?: 'light' | 'dark'; }

const MasterclassRegistrationForm: React.FC<Props> = ({ variant = 'dark' }) => {
  const [formData, setFormData] = useState({ full_name: '', email: '', business_type: '', biggest_challenge: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'duplicate' | 'error'>('idle');
  const light = variant === 'light';
  const inputVariant = light ? 'light' : 'dark';
  const labelVariant = light ? 'light' : 'dark';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.full_name || !formData.email || !formData.business_type) return;
    setStatus('loading');
    try {
      const result = await masterclassService.register(formData);
      setStatus(result.duplicate ? 'duplicate' : result.success ? 'success' : 'error');
    } catch { setStatus('error'); }
  };

  const cardVariant = light ? 'light' as const : 'dark' as const;

  if (status === 'success' || status === 'duplicate') {
    const isSuccess = status === 'success';
    return (
      <Card variant={cardVariant} padding="lg" className="max-w-lg mx-auto text-center shadow-lg">
        <IconBox
          variant={light ? 'light' : 'dark'}
          size="xl"
          className="mx-auto mb-5"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isSuccess ? 'M5 13l4 4L19 7' : 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'} />
          </svg>
        </IconBox>
        <h3 className={`text-xl font-bold mb-2 ${light ? 'text-gray-900' : 'text-white'}`}>
          {isSuccess ? "You're on the list!" : "You're already registered!"}
        </h3>
        <p className="text-gray-500 text-sm">
          {isSuccess ? "We'll reach out with cohort details and early-bird pricing." : "We have your details. We'll be in touch."}
        </p>
      </Card>
    );
  }

  return (
    <Card variant={cardVariant} padding="lg" className="max-w-lg mx-auto shadow-lg">
      <h3 className={`text-xl font-bold mb-1 text-center ${light ? 'text-gray-900' : 'text-white'}`}>Register Your Interest</h3>
      <p className="text-sm text-center mb-8 text-gray-500">Early registrants get priority access and pricing.</p>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label variant={labelVariant}>Full Name</Label>
          <Input variant={inputVariant} type="text" required value={formData.full_name}
            onChange={e => setFormData(p => ({ ...p, full_name: e.target.value }))} placeholder="Your name" />
        </div>
        <div>
          <Label variant={labelVariant}>Email</Label>
          <Input variant={inputVariant} type="email" required value={formData.email}
            onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="you@business.com" />
        </div>
        <div>
          <Label variant={labelVariant}>Business Type</Label>
          <Select variant={inputVariant} required value={formData.business_type}
            onChange={e => setFormData(p => ({ ...p, business_type: e.target.value }))}>
            <option value="" disabled>Select your business type</option>
            {businessTypes.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
          </Select>
        </div>
        <div>
          <Label variant={labelVariant}>
            Biggest AI challenge <span className="text-gray-400 font-normal">(optional)</span>
          </Label>
          <Textarea variant={inputVariant} value={formData.biggest_challenge} rows={3}
            onChange={e => setFormData(p => ({ ...p, biggest_challenge: e.target.value }))}
            placeholder="What's your biggest struggle with AI?" />
        </div>
        <Button type="submit" disabled={status === 'loading'} className="w-full rounded-xl h-12 text-[15px] font-semibold">
          {status === 'loading' ? 'Registering...' : 'Reserve My Spot'}
        </Button>
        {status === 'error' && <p className="text-red-500 text-sm text-center">Something went wrong. Please try again.</p>}
      </form>
    </Card>
  );
};

export default MasterclassRegistrationForm;
